import { cloneDeep } from 'lodash';
import React from 'react';
import { withRouter } from 'found';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import styles from '../Document.scss';
import Actions from './Actions';
import Header from './Header';
import MobileSidebar from 'react-sidebar';
import Sidebar from '../../App/SidebarComponent';
import NotFound from '../../App/404';
import { Loader } from '../../Elements';
import {
  Analytics,
  GlobalNotifications,
  Intent,
  MobileModal
} from '../../Modules';
import Brand from './Brand';
import Logo from './Logo';
import Description from './Description';
import Notes from './Notes';
import { Button, Container, Divider, Grid, Rail, Segment } from 'semantic-ui-react';
import PlaylistPreview from '../../Playlist/Preview';
import { RootConsumer } from '../../RootComponent';
import { arrayMove } from '../../../lib/utils/array';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

// eslint-disable-next-line new-cap
const SortableItem = SortableElement(PlaylistPreview);

// eslint-disable-next-line new-cap
const SortableList = SortableContainer(({ items, openPlaylist }) =>
  <div>
    {items.map(({ node: playlist }, index) =>
      <SortableItem
        playlist={playlist}
        index={index}
        openPlaylist={openPlaylist}
        key={index}
      />
    )}
  </div>
);

const MusicPlanner = class MusicPlanner extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    node: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    params: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.canEdit = this.canEdit.bind(this);
    this.onSetSidebarOpen = this.onSetSidebarOpen.bind(this);
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.openPlaylist = this.openPlaylist.bind(this);
    this.closePlaylist = this.closePlaylist.bind(this);
    this.createPlannerPlaylist = this.createPlannerPlaylist.bind(this);
    this.reorderPlannerPlaylists = this.reorderPlannerPlaylists.bind(this);

    this.state = {
      error: !this.props.node,
      sidebarOpen: false
    };
  }

  componentWillMount() {
    if (!this.props.node && this.props.location.query.share_token) {
      this.addMusicPlannerToSession();
    }
  }

  onSetSidebarOpen(open) {
    this.setState({ sidebarOpen: open });
  }

  addMusicPlannerToSession() {
    this.setState({ error: false });
    const onCompleted = () => {
      this.props.relay.refetch(fragmentVariables => fragmentVariables, null);
    };

    const onError = () => {
      this.setState({ error: true });
    };

    const mutation = graphql`
      mutation MusicPlannerComponent_addMusicPlannerToSessionMutation(
        $input: AddMusicPlannerToSessionInput!
      ) {
        addMusicPlannerToSession(input: $input) {
          music_planner {
            id
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const { share_token } = this.props.location.query;

    commitMutation(environment, {
      mutation,
      variables: { input: { share_token } },
      onCompleted,
      onError
    });
  }

  createPlannerPlaylist() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to add playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully added playlist',
        intent: Intent.SUCCESS
      });
      this.openPlaylist(
        response.createPlannerPlaylist.new_planner_playlist_edge.node.id
      );
      Analytics.trackEvent('created-planner-playlist');
    };

    const mutation = graphql`
      mutation MusicPlannerComponent_createPlannerPlaylistMutation(
        $input: CreatePlannerPlaylistInput!
      ) {
        createPlannerPlaylist(input: $input) {
          new_planner_playlist_edge {
            node {
              id
              ...PlannerPlaylistContainer_node
            }
          }
        }
      }
    `;

    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_planner_playlist_edge',
        connectionInfo: [
          {
            key: 'MusicPlannerContainer_planner_playlists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          music_planner_id: parentID,
          title: '(Untitled Playlist)'
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  reorderPlannerPlaylists({ oldIndex, newIndex }) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update playlist order',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      Analytics.trackEvent('updated-planner-playlist-position');
    };

    const mutation = graphql`
      mutation MusicPlannerComponent_UpdatePlannerPlaylistMutation(
        $input: UpdatePlannerPlaylistInput!
      ) {
        updatePlannerPlaylist(input: $input) {
          music_planner {
            ...MusicPlannerContainer_node
          }
        }
      }
    `;

    const musicPlanner = cloneDeep(this.props.node);
    const movedPlaylistId =
      musicPlanner.planner_playlists.edges[oldIndex].node.id;
    musicPlanner.planner_playlists.edges = arrayMove(
      musicPlanner.planner_playlists.edges,
      oldIndex,
      newIndex
    );

    const optimisticResponse = {
      updatePlannerPlaylist: {
        music_planner: musicPlanner
      }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: { id: movedPlaylistId, position: newIndex + 1 }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  toggleSidebar(ev) {
    ev.preventDefault();
    this.onSetSidebarOpen(!this.state.sidebarOpen);
  }

  canEdit() {
    return this.props.node.can_edit && this.props.params.action === 'edit';
  }

  openPlaylist(id) {
    this.props.router.push(
      `/docs/m/${this.props.node.id}/${this.props.params.action}/playlist/${id}`
    );
  }

  closePlaylist() {
    this.props.router.go(-1);
  }

  render() {
    const { node, params, relay, router, viewer } = this.props;
    const playlistOpen = this.props.params.playlist_id !== undefined;

    const ActionsMenu = (
      <RootConsumer>
        {({ window }) =>
          <Actions
            node={node}
            params={params}
            relay={relay}
            router={router}
            windowDimensions={window}
          />}
      </RootConsumer>
    );

    return (
      <RootConsumer>
        {({ isComputer, isMobile, isLargeScreen, window }) =>
          <div>
            {node &&
              <MobileSidebar
                sidebar={
                  <Sidebar
                    setSidebarOpenState={this.onSetSidebarOpen}
                    router={router}
                    viewer={viewer}
                  />
                }
                sidebarClassName={styles.sidebar}
                open={this.state.sidebarOpen}
                onSetOpen={this.onSetSidebarOpen}
                touch={false}
                contentClassName={styles.mobileSidebarContent}
                rootClassName={styles.mobileSidebarRoot}
              >
                <Header
                  toggleSidebar={this.toggleSidebar}
                  sidebarOpen={this.state.sidebarOpen}
                  document={node}
                  relay={relay}
                  router={router}
                  params={params}
                  viewer={viewer}
                  windowDimensions={window}
                />
                <Container className={styles.container}>
                  {!isLargeScreen &&
                    isComputer &&
                    <Grid>
                      <Grid.Row
                        columns='equal'
                        className={styles.actionsMenuTablet}
                      >
                        <Grid.Column>
                          {ActionsMenu}
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>}
                  <Segment
                    className={styles.segment}
                    style={{ maxWidth: '720px' }}
                  >
                    {isLargeScreen &&
                      <Rail
                        position='right'
                        close
                        className={styles.actionsMenuDesktop}
                      >
                        {ActionsMenu}
                      </Rail>}
                    <Grid padded={!isMobile} relaxed={!isMobile}>
                      <Grid.Row columns='equal'>
                        <Grid.Column>
                          <Logo
                            canEdit={this.canEdit()}
                            viewer={viewer}
                            node={node}
                            relay={relay}
                          />
                          <Brand
                            canEdit={this.canEdit()}
                            viewer={viewer}
                            node={node}
                            relay={relay}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      {playlistOpen &&
                        <Grid.Row columns='equal'>
                          <Grid.Column>
                            <a onClick={this.closePlaylist}>
                              &lt; Back to All Playlists
                            </a>
                          </Grid.Column>
                        </Grid.Row>}
                      <Grid.Row columns='equal'>
                        <Grid.Column>
                          {this.props.children}
                        </Grid.Column>
                      </Grid.Row>
                      {!playlistOpen &&
                        <Grid.Row>
                          <Grid.Column>
                            <Description
                              node={this.props.node}
                              relay={this.props.relay}
                              canEdit={this.canEdit()}
                            />
                          </Grid.Column>
                        </Grid.Row>}
                      <Grid.Row columns='equal'>
                        <Grid.Column>
                          {!playlistOpen &&
                            this.canEdit() &&
                            <SortableList
                              items={node.planner_playlists.edges}
                              onSortEnd={this.reorderPlannerPlaylists}
                              helperClass={styles.sortableHelper}
                              openPlaylist={this.openPlaylist}
                              pressDelay={200}
                            />}
                          {!playlistOpen &&
                            !this.canEdit() &&
                            node.planner_playlists.edges.map(
                              ({ node: plannerPlaylist }, index) =>
                                <PlaylistPreview
                                  playlist={plannerPlaylist}
                                  openPlaylist={this.openPlaylist}
                                  key={index}
                                />
                            )}
                          {isMobile &&
                            <MobileModal
                              open={playlistOpen}
                              onClose={this.closePlaylist}
                            >
                              <MobileModal.Content>
                                {this.props.children}
                              </MobileModal.Content>
                            </MobileModal>}
                          {node.planner_playlists.edges.length === 0 &&
                            <div
                              style={{ textAlign: 'center', padding: '2em' }}
                            >
                              <h3>This music list has no playlists</h3>
                              {this.canEdit() &&
                                <Button
                                  primary
                                  icon='plus'
                                  labelPosition='left'
                                  content='Add Playlist'
                                  onClick={this.createPlannerPlaylist}
                                />}
                            </div>}
                          {!playlistOpen &&
                            this.canEdit() &&
                            node.planner_playlists.edges.length !== 0 &&
                            <Button
                              primary
                              icon='plus'
                              labelPosition='left'
                              content='Add Playlist'
                              onClick={this.createPlannerPlaylist}
                            />}
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Divider horizontal className={styles.heading}>
                            Notes
                          </Divider>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Notes node={node} relay={relay} />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                </Container>
              </MobileSidebar>}
            {!node &&
              <div>
                {this.state.error && <NotFound viewer={viewer} />}
                {!this.state.error && <Loader />}
              </div>}
          </div>}
      </RootConsumer>
    );
  }
};

export default withRouter(MusicPlanner);



// WEBPACK FOOTER //
// ./webpack/components/Document/MusicPlanner/MusicPlannerComponent.js